import { ingredientById } from "../data/ingredients";
import { recipeById } from "../data/recipes";
import { candidateForRecipe, selectionFromState } from "../domain/candidates";
import { fillRecipeSlots } from "../domain/instantiateRecipe";
import {
  COOKING_LOCATIONS,
  EMPTY_APP_STATE,
  FOOD_TYPES,
  type AppState,
  type Candidate,
  type CookingLocationId,
  type FoodTypeId,
  type RecipeFamily,
  type SelectionState,
} from "../domain/types";

export interface ParsedUrlState {
  state: AppState;
  message: string | null;
}

function isFoodTypeId(value: unknown): value is FoodTypeId {
  return FOOD_TYPES.some((option) => option.id === value);
}

function isCookingLocationId(value: unknown): value is CookingLocationId {
  return COOKING_LOCATIONS.some((option) => option.id === value);
}

function normalizeSelection(selection: SelectionState): SelectionState {
  return {
    foodTypeIds: [
      ...new Set(
        (Array.isArray(selection.foodTypeIds)
          ? selection.foodTypeIds
          : []
        ).filter(isFoodTypeId),
      ),
    ].sort(),
    cookingLocationIds: [
      ...new Set(
        (Array.isArray(selection.cookingLocationIds)
          ? selection.cookingLocationIds
          : []
        ).filter(isCookingLocationId),
      ),
    ].sort(),
    ingredientIds: [
      ...new Set(
        selection.ingredientIds.filter((id) => {
          const ingredient = ingredientById.get(id);
          return ingredient && !ingredient.assumedStaple;
        }),
      ),
    ].sort(),
    veganOnly: selection.veganOnly === true,
  };
}

function normalizeSwaps(
  recipe: RecipeFamily,
  candidate: Candidate,
  entries: Iterable<readonly [string, string]>,
  veganOnly: boolean,
): Record<string, string> {
  const swaps: Record<string, string> = {};
  const defaultSlots = fillRecipeSlots(recipe, candidate, {}, veganOnly);
  const sortedEntries = [...entries].sort(
    ([slotA, ingredientA], [slotB, ingredientB]) =>
      `${slotA}:${ingredientA}`.localeCompare(`${slotB}:${ingredientB}`),
  );

  for (const [slotId, ingredientId] of sortedEntries) {
    const slot = recipe.slots.find((item) => item.id === slotId);
    const ingredient = ingredientById.get(ingredientId);
    if (!slot || !ingredient || candidate.slotAssignment[slotId]) continue;
    if (!slot.options.some((option) => option.ingredientId === ingredientId))
      continue;
    if (veganOnly && !ingredient.dietTags.includes("vegan")) continue;
    if (defaultSlots?.get(slotId)?.ingredientId === ingredientId) continue;
    swaps[slotId] = ingredientId;
  }

  return swaps;
}

export function normalizeAppState(state: AppState): AppState {
  const selection = normalizeSelection(state);
  const normalized: AppState = {
    ...selection,
    recipeId: null,
    swaps: {},
  };
  if (!state.recipeId) return normalized;

  const recipe = recipeById.get(state.recipeId);
  const candidate = recipe
    ? candidateForRecipe(recipe.id, selectionFromState(selection))
    : null;
  if (!recipe || !candidate) return normalized;

  normalized.recipeId = recipe.id;
  normalized.swaps = normalizeSwaps(
    recipe,
    candidate,
    Object.entries(state.swaps),
    selection.veganOnly,
  );
  return normalized;
}

export function parseUrlState(search: string): ParsedUrlState {
  const params = new URLSearchParams(search);
  const foodTypeIds = (params.get("type") ?? "").split(",").filter(Boolean);
  const cookingLocationIds = (params.get("cook") ?? "")
    .split(",")
    .filter(Boolean);
  const ingredientIds = (params.get("i") ?? "").split(",").filter(Boolean);

  const state: AppState = {
    ...EMPTY_APP_STATE,
    swaps: {},
    ...normalizeSelection({
      foodTypeIds: foodTypeIds as FoodTypeId[],
      cookingLocationIds: cookingLocationIds as CookingLocationId[],
      ingredientIds,
      veganOnly: params.get("diet") === "vegan",
    }),
  };

  const requestedRecipe = params.get("recipe");
  if (!requestedRecipe) return { state, message: null };

  const recipe = recipeById.get(requestedRecipe);
  const candidate = candidateForRecipe(
    requestedRecipe,
    selectionFromState(state),
  );
  if (!recipe || !candidate)
    return { state, message: "Recipe no longer available" };

  state.recipeId = requestedRecipe;
  state.swaps = normalizeSwaps(
    recipe,
    candidate,
    (params.get("swap") ?? "")
      .split(",")
      .filter(Boolean)
      .map((serializedSwap) => {
        const [slotId = "", ingredientId = ""] = serializedSwap.split(":");
        return [slotId, ingredientId] as const;
      }),
    state.veganOnly,
  );

  return { state, message: null };
}

export function serializeUrlState(state: AppState): string {
  const normalized = normalizeAppState(state);
  const params = new URLSearchParams();
  if (normalized.foodTypeIds.length)
    params.set("type", normalized.foodTypeIds.join(","));
  if (normalized.cookingLocationIds.length)
    params.set("cook", normalized.cookingLocationIds.join(","));
  if (normalized.ingredientIds.length)
    params.set("i", normalized.ingredientIds.join(","));
  if (normalized.veganOnly) params.set("diet", "vegan");
  if (normalized.recipeId) params.set("recipe", normalized.recipeId);
  const serializedSwaps = Object.entries(normalized.swaps)
    .sort(([slotA], [slotB]) => slotA.localeCompare(slotB))
    .map(([slotId, ingredientId]) => `${slotId}:${ingredientId}`);
  if (serializedSwaps.length) params.set("swap", serializedSwaps.join(","));
  const query = params.toString();
  return query ? `/?${query}` : "/";
}
